import { useState } from 'react'

const EMPTY_TERM = {
  name: '',
  startDate: '',
  endDate: '',
}

function TermForm({ initial, onSave, onCancel, submitLabel }) {
  const [form, setForm] = useState(initial)

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) return
    onSave(form)
  }

  return (
    <form className="term-form" onSubmit={handleSubmit}>
      <div className="class-form-row">
        <label className="class-form-label">
          Term Name
          <input
            className="task-input"
            type="text"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="e.g. Spring 2026"
            autoFocus
          />
        </label>
      </div>
      <div className="class-form-row class-form-row--inline">
        <label className="class-form-label">
          Start Date
          <input
            className="task-input"
            type="date"
            value={form.startDate}
            onChange={(e) => update('startDate', e.target.value)}
          />
        </label>
        <label className="class-form-label">
          End Date
          <input
            className="task-input"
            type="date"
            value={form.endDate}
            onChange={(e) => update('endDate', e.target.value)}
          />
        </label>
      </div>
      <div className="class-form-actions">
        <button type="submit" className="btn btn-primary" disabled={!form.name.trim()}>
          {submitLabel || 'Save'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function TermManager({ terms, activeTermId, onAddTerm, onUpdateTerm, onDeleteTerm, onSwitchTerm }) {
  const [showForm, setShowForm] = useState(false)
  const [editingTerm, setEditingTerm] = useState(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)

  function handleCreate(form) {
    onAddTerm({ ...form, id: Date.now().toString() })
    setShowForm(false)
  }

  function handleUpdate(form) {
    onUpdateTerm(editingTerm.id, form)
    setEditingTerm(null)
  }

  function handleEdit(term) {
    setEditingTerm(term)
    setShowForm(false)
    setConfirmDeleteId(null)
  }

  function handleDelete(termId) {
    if (confirmDeleteId !== termId) {
      setConfirmDeleteId(termId)
      return
    }
    onDeleteTerm(termId)
    setConfirmDeleteId(null)
  }

  return (
    <div>
      <h2 className="page-title">Terms</h2>
      <p className="page-description">Manage your academic terms. Each term has its own classes, tasks, and medications.</p>

      {!showForm && !editingTerm && (
        <button className="btn btn-primary class-add-btn" onClick={() => setShowForm(true)}>
          + New Term
        </button>
      )}

      {showForm && (
        <div className="class-form-card">
          <h3 className="class-form-title">Create Term</h3>
          <TermForm
            initial={EMPTY_TERM}
            onSave={handleCreate}
            onCancel={() => setShowForm(false)}
            submitLabel="Create Term"
          />
        </div>
      )}

      {editingTerm && (
        <div className="class-form-card">
          <h3 className="class-form-title">Edit Term</h3>
          <TermForm
            key={editingTerm.id}
            initial={editingTerm}
            onSave={handleUpdate}
            onCancel={() => setEditingTerm(null)}
          />
        </div>
      )}

      <div className="term-list">
        {terms.map((term) => {
          const isActive = term.id === activeTermId
          const isLastTerm = terms.length === 1
          return (
            <div key={term.id} className={`term-card ${isActive ? 'term-card--active' : ''}`}>
              <div className="term-card-header">
                <div className="term-card-info">
                  <h3 className="term-card-name">
                    {term.name}
                    {isActive && <span className="term-active-badge">Active</span>}
                  </h3>
                  {(term.startDate || term.endDate) && (
                    <span className="term-card-dates">
                      {formatDate(term.startDate)}
                      {term.startDate && term.endDate && ' – '}
                      {formatDate(term.endDate)}
                    </span>
                  )}
                </div>
                <div className="term-card-actions">
                  {confirmDeleteId === term.id ? (
                    <div className="delete-confirm">
                      <span className="delete-confirm-text">Delete?</span>
                      <button className="btn-confirm-yes" onClick={() => handleDelete(term.id)}>Yes</button>
                      <button className="btn-confirm-no" onClick={() => setConfirmDeleteId(null)}>No</button>
                    </div>
                  ) : (
                    <>
                      {!isActive && (
                        <button className="btn btn-sm btn-primary" onClick={() => onSwitchTerm(term.id)}>
                          Switch
                        </button>
                      )}
                      <button className="btn-icon btn-edit-class" style={{ opacity: 1 }} onClick={() => handleEdit(term)} title="Edit">&#9998;</button>
                      {!isActive && !isLastTerm && (
                        <button className="btn-icon btn-delete-task" onClick={() => handleDelete(term.id)} title="Delete">&#10005;</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TermManager
